import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { H1, H3, H5, Label, P } from '../components/text';
import { IMenuItem } from '../models/menu-item';
import { ISponsor } from '../models/sponsor';
import { IPage } from '../models/page';
import { IFeaturedImage } from '../models/featured-image';
import PostBody from '../components/post-body';
import { ITransfer } from '@/models/transfer';

interface IWinners {
	winnaar: string;
	aantal: string;
	clublogo: {
		sourceUrl: string;
		altText: string;
	};
}
interface RankedPlayer extends IWinners {
	ranking: number;
}

interface IProps {
	primaryMenuItems: IMenuItem[];
	pageData: IPage;
	sponsors: ISponsor[];
	winnaars: IWinners[];
	coverImage: IFeaturedImage;
	players?: ITransfer[];
}

export default function SpelerVanDeWeekPage({
	primaryMenuItems,
	pageData: { page },
	sponsors,
	winnaars,
	coverImage,
	players,
}: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	function createRanking(winnaars: IWinners[]): RankedPlayer[] {
		if (!winnaars || winnaars.length === 0) {
			return [];
		}

		const sortedPlayers = [...winnaars].sort((a, b) => Number(b.aantal) - Number(a.aantal));

		let ranking = 1;
		let previousAantal = Number(sortedPlayers[0]?.aantal);
		const rankedPlayers = sortedPlayers.map((player, index) => {
			if (index > 0 && Number(player.aantal) !== previousAantal) {
				ranking = index + 1;
			}
			previousAantal = Number(player.aantal);
			return { ...player, ranking };
		});

		return rankedPlayers;
	}

	const rankedPlayers = createRanking(winnaars);

	function getSeasonString() {
		const now = new Date();
		const year = now.getFullYear();
		const month = now.getMonth() + 1; // January is 0

		const short = (y: number) => `'${(y % 100).toString().padStart(2, '0')}`;

		if (month >= 8) {
			return `${short(year)}/${short(year + 1)}`;
		} else {
			return `${short(year - 1)}/${short(year)}`;
		}
	}

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			{coverImage && (
				<div className="relative h-auto lg:h-[540px] w-full">
					<img
						src={coverImage.node.sourceUrl}
						alt={coverImage.node.altText}
						className="object-cover object-top aspect-video-16/9 w-full h-full"
					/>
					<div className="kkd-gradient-red-overlay z-0"></div>
					<div className="kkd-gradient-dark-overlay z-0"></div>

					<Container>
						<div className="absolute top-1/2 transform -translate-y-1/2 flex flex-col z-30">
							<div className="flex items-center">
								<div className="flex-grow">
									<H1 className="text-white mb-2 drop-shadow-md ">
										{page.title || 'Stem op de Speler van de Week!'}
									</H1>
									<P className="hidden lg:block max-w-7xl text-white pr-12">
										Dit seizoen kan er elke week gestemd worden op de Speler van de Week! Op basis
										van statistieken bestaande uit o.a. goals, clean sheets, succesvolle dribbels,
										succesvolle passes etc. worden er drie spelers genomineerd voor de titel Speler
										van de Week. Breng nu je stem uit en maak kans op twee wedstrijdtickets voor een
										Keuken Kampioen Divisie-wedstrijd!
									</P>
								</div>
							</div>
						</div>
					</Container>
				</div>
			)}
			<Container className="px-5 py-12 xl:py-16 ">
				<section className="mb-12">
					<Container>
						<div className="flex flex-col lg:flex-row">
							<div className="w-full lg:w-4/12 xl:w-3/12 h-full order-2 lg:order-none">
								{winnaars && winnaars.length > 0 && (
									<>
										<div className="mb-8 text-center">
											<H5>Winnaars</H5>
											<H5>{`Spelers van de Week ${getSeasonString()}`}</H5>
										</div>

										<>
											<div className="flex justify-between mb-1">
												<div className="gap-10 flex">
													<P className="ml-4">#</P>
													<P className="">Speler</P>
												</div>
												<P className="mr-2">Awards</P>
											</div>
											<ul>
												{rankedPlayers.map((player, index) => (
													<React.Fragment key={`${player}-${index}`}>
														{player.winnaar ? (
															<li className="col-span-1 flex mb-1">
																<div className="flex flex-1 items-center justify-between truncate bg-white">
																	<div className="flex-1 truncate px-4 py-3 flex items-center gap-2">
																		<Label>{player.ranking.toString()}</Label>
																		{player.clublogo && (
																			<Image
																				src={player.clublogo.sourceUrl}
																				alt={player.clublogo.altText}
																				width={32}
																				height={32}
																				className="inline-block h-8 w-8 aspect-square object-contain"
																			/>
																		)}
																		<div className="flex flex-col truncate">
																			<Label className="!text-sm">
																				{player.winnaar}
																			</Label>
																		</div>
																	</div>

																	<div className="flex w-16 h-full shrink-0 items-center justify-center bg-kkd-red">
																		<Label className="text-white">
																			{player.aantal}
																		</Label>
																	</div>
																</div>
															</li>
														) : (
															<li
																className="bg-gray-50"
																key={`${player.winnaar}-${index}`}
															>
																<P className="text-center normal-case">
																	Er zijn nog geen winnaar(s) benoemd
																</P>
															</li>
														)}
													</React.Fragment>
												))}
											</ul>
										</>
									</>
								)}
							</div>
							<div className="flex-1 lg:pl-6 xl:pl-16 w-full lg:w-8/12 xl:w-9/12">
								<section className="mb-12">
									{page.content && <PostBody content={page.content} />}
								</section>
								<section className="mb-12">
									{winnaars ? (
										<H3 className="my-4">Alle winnaars</H3>
									) : (
										<H3 className="my-4">Er zijn nog geen winnaar(s) benoemd</H3>
									)}
									<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
										{players &&
											players.map((player) => (
												<div className="mx-auto" key={player.id}>
													<Image
														alt={player.altText}
														src={player.sourceUrl}
														className=""
														loading="lazy"
														width={600}
														height={600}
													/>
												</div>
											))}
									</div>
								</section>
							</div>
						</div>
					</Container>
				</section>
			</Container>
		</Layout>
	);
}

interface IApiProps {
	page: IPage & {
		spelersVanDeWeek: { winnaars: IWinners[] };
		spelerVanDeWeek: { spelers: ITransfer[] };
		featuredImage: IFeaturedImage;
	};
	sponsors: { nodes: ISponsor[] };
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();

	const pageData = await fetchAPI<IApiProps>(
		`
		query {
			page(id: 114056, idType: DATABASE_ID) {
				title
				content
				featuredImage {
					node {
						altText
						sourceUrl
					}
				}
				spelersVanDeWeek {
					winnaars {
						winnaar
						aantal
						clublogo {
							altText
							sourceUrl
							sizes(size: MEDIUM)
						}
					}
				}
				spelerVanDeWeek {
					spelers {
						altText
						id
						sourceUrl(size: MEDIUM)
						date
					}
				}
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	return {
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
			players: pageData.page.spelerVanDeWeek.spelers,
			winnaars: pageData.page.spelersVanDeWeek.winnaars,
			coverImage: pageData.page.featuredImage,
		},
		revalidate: 300,
	};
};
